import { useCallback, useState } from 'react';
import { z } from 'zod';

import { getEmailAvailable } from '@/features/auth/api';
import {
  EmailValidationMessages,
  ForgotPasswordEmailValidationMessages,
  InvalidEmail,
} from '@/features/auth/constants';

type CheckUsernameResponse = {
  type: 'success' | 'error' | null;
  message: string | null;
};

const emailSchema = z.string().email();

export const useCheckEmail = (reverse = false) => {
  const [response, setResponse] = useState<CheckUsernameResponse>({
    type: null,
    message: null,
  });

  const checkEmail = useCallback(
    async (email: string) => {
      const emailResult = emailSchema.safeParse(email);

      if (emailResult.error) {
        setResponse({
          type: 'error',
          message: InvalidEmail,
        });

        return;
      }

      try {
        const response = await getEmailAvailable(email);

        let available = response.available;

        if (reverse) {
          available = !available;
        }

        const type = available ? 'success' : 'error';
        const message = reverse
          ? ForgotPasswordEmailValidationMessages[type]
          : EmailValidationMessages[type];

        setResponse({
          message,
          type,
        });
      } catch {
        setResponse({
          type: 'error',
          message: 'An error occurred while checking email',
        });
      }
    },
    [reverse],
  );

  return { checkEmail, response };
};
